/**
 * Bulk Control Management Page
 *
 * Allows users to add multiple controls to a framework easily.
 * Supports:
 * - Adding controls one by one with a form
 * - Bulk import via textarea (paste multiple controls)
 * - View and edit existing controls
 */

import { useState, useMemo, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { z } from 'zod/v3';
import {
  ChevronRight,
  Plus,
  Trash2,
  Loader2,
  FileText,
  AlertCircle,
  Pencil,
} from 'lucide-react';
import { toast } from 'sonner';

import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import MainHeader from '@/components/layout/MainHeader';
import { Main } from '@/components/main';

const controlsSearchSchema = z.object({
  tab: z.enum(['add', 'bulk', 'existing', 'edit']).optional(),
});

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/compliance/$frameworkId/controls/'
)({
  validateSearch: controlsSearchSchema,
  component: ControlsManagementPage,
});

interface ControlInput {
  id: string; // temp id for UI
  controlId: string;
  title: string;
  description: string;
}

function ControlsManagementPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { workspaceId, frameworkId } = Route.useParams();
  const { tab } = Route.useSearch();

  // Tab state - use URL param as initial value
  const [activeTab, setActiveTab] = useState<'add' | 'bulk' | 'existing' | 'edit'>(tab || 'add');

  // Framework edit state
  const [frameworkName, setFrameworkName] = useState('');
  const [frameworkDescription, setFrameworkDescription] = useState('');
  const [isFrameworkDirty, setIsFrameworkDirty] = useState(false);

  // Single control form state
  const [controlId, setControlId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  // Bulk controls state
  const [bulkText, setBulkText] = useState('');
  const [parsedControls, setParsedControls] = useState<ControlInput[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);

  // Fetch framework details
  const frameworkQuery = useQuery({
    ...trpc.guidedTemplate.getFramework.queryOptions({ id: frameworkId }),
    enabled: !!frameworkId,
  });

  // Fetch existing controls for this framework
  const controlsTreeQuery = useQuery({
    ...trpc.compliance.getControlsTree.queryOptions({ frameworkId }),
    enabled: !!frameworkId,
  });

  const framework = frameworkQuery.data;

  // Extract existing controls from tree
  const existingControls = useMemo(() => {
    const tree = controlsTreeQuery.data?.tree || [];
    if (tree.length === 0) return [];

    const controls: Array<{
      id: string;
      controlId: string;
      name: string;
      description?: string;
      isCustom: boolean;
    }> = [];

    // Tree structure: framework > category > control
    const frameworkNode = tree[0];
    frameworkNode?.children?.forEach((category) => {
      category.children?.forEach((control) => {
        // Cast to access extended properties
        const extControl = control as typeof control & { description?: string; isCustom?: boolean };
        controls.push({
          id: control.id,
          controlId: control.controlId || '',
          name: control.name,
          description: extControl.description,
          isCustom: !!extControl.isCustom,
        });
      });
    });

    return controls;
  }, [controlsTreeQuery.data]);

  // Create control mutation
  const createControlMutation = useMutation({
    ...trpc.guidedTemplate.createControl.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: trpc.compliance.getControlsTree.queryKey({ frameworkId }),
      });
    },
  });

  // Delete control mutation
  const deleteControlMutation = useMutation({
    ...trpc.guidedTemplate.deleteControl.mutationOptions(),
    onSuccess: () => {
      toast.success('Control deleted');
      queryClient.invalidateQueries({
        queryKey: trpc.compliance.getControlsTree.queryKey({ frameworkId }),
      });
    },
    onError: (error) => {
      toast.error(`Failed to delete control: ${error.message}`);
    },
  });

  // Update framework mutation
  const updateFrameworkMutation = useMutation({
    ...trpc.guidedTemplate.updateFramework.mutationOptions(),
    onSuccess: () => {
      toast.success('Framework updated');
      setIsFrameworkDirty(false);
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.getFramework.queryKey({ id: frameworkId }),
      });
    },
    onError: (error) => {
      toast.error(`Failed to update framework: ${error.message}`);
    },
  });

  // Sync framework data to local state when loaded
  useEffect(() => {
    if (framework) {
      setFrameworkName(framework.name);
      setFrameworkDescription(framework.description || '');
    }
  }, [framework]);

  // Track if framework form is dirty
  const handleFrameworkNameChange = (value: string) => {
    setFrameworkName(value);
    setIsFrameworkDirty(value !== framework?.name || frameworkDescription !== (framework?.description || ''));
  };

  const handleFrameworkDescriptionChange = (value: string) => {
    setFrameworkDescription(value);
    setIsFrameworkDirty(frameworkName !== framework?.name || value !== (framework?.description || ''));
  };

  // Save framework changes
  const handleSaveFramework = async () => {
    if (!frameworkName.trim()) {
      toast.error('Framework name is required');
      return;
    }
    await updateFrameworkMutation.mutateAsync({
      id: frameworkId,
      name: frameworkName.trim(),
      description: frameworkDescription.trim() || undefined,
    });
  };

  // Handle single control creation
  const handleAddControl = async () => {
    if (!controlId.trim()) {
      toast.error('Control ID is required');
      return;
    }
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!description.trim()) {
      toast.error('Description is required');
      return;
    }

    try {
      await createControlMutation.mutateAsync({
        frameworkId,
        controlId: controlId.trim(),
        title: title.trim(),
        description: description.trim(),
      });
      toast.success(`Control ${controlId} added`);
      setControlId('');
      setTitle('');
      setDescription('');
    } catch (error) {
      toast.error(`Failed to add control: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Parse bulk text into controls
  const handleParseBulk = () => {
    if (!bulkText.trim()) {
      setParseError('Please enter some text to parse');
      setParsedControls([]);
      return;
    }

    const lines = bulkText.trim().split('\n');
    const controls: ControlInput[] = [];
    const errors: string[] = [];

    let currentControl: Partial<ControlInput> | null = null;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) {
        // Empty line - save current control if valid
        if (currentControl?.controlId && currentControl?.title) {
          controls.push({
            id: `temp-${Date.now()}-${controls.length}`,
            controlId: currentControl.controlId,
            title: currentControl.title,
            description: currentControl.description || currentControl.title,
          });
        }
        currentControl = null;
        continue;
      }

      // Try to parse as "ID - Title" or "ID: Title" format
      const match = line.match(/^([A-Za-z0-9.-]+)\s*[-:]\s*(.+)$/);
      if (match) {
        // Save previous control if exists
        if (currentControl?.controlId && currentControl?.title) {
          controls.push({
            id: `temp-${Date.now()}-${controls.length}`,
            controlId: currentControl.controlId,
            title: currentControl.title,
            description: currentControl.description || currentControl.title,
          });
        }
        currentControl = {
          controlId: match[1].trim(),
          title: match[2].trim(),
          description: '',
        };
      } else if (currentControl) {
        // Append to description of current control
        currentControl.description = currentControl.description
          ? `${currentControl.description} ${line}`
          : line;
      } else {
        // Try to treat the line as just a control ID with title
        const simpleMatch = line.match(/^([A-Za-z0-9.-]+)\s+(.+)$/);
        if (simpleMatch) {
          currentControl = {
            controlId: simpleMatch[1].trim(),
            title: simpleMatch[2].trim(),
            description: '',
          };
        } else {
          errors.push(`Line ${i + 1}: Could not parse "${line.substring(0, 50)}..."`);
        }
      }
    }

    // Don't forget the last control
    if (currentControl?.controlId && currentControl?.title) {
      controls.push({
        id: `temp-${Date.now()}-${controls.length}`,
        controlId: currentControl.controlId,
        title: currentControl.title,
        description: currentControl.description || currentControl.title,
      });
    }

    if (controls.length === 0) {
      setParseError('No controls could be parsed. Use format: "CTRL-1 - Control Title" or "CTRL-1: Control Title"');
      setParsedControls([]);
    } else {
      setParseError(errors.length > 0 ? errors.join('\n') : null);
      setParsedControls(controls);
    }
  };

  // Remove a parsed control
  const handleRemoveParsedControl = (id: string) => {
    setParsedControls((prev) => prev.filter((c) => c.id !== id));
  };

  // Edit a parsed control
  const handleEditParsedControl = (id: string, field: keyof ControlInput, value: string) => {
    setParsedControls((prev) =>
      prev.map((c) => (c.id === id ? { ...c, [field]: value } : c))
    );
  };

  // Import all parsed controls
  const [isImporting, setIsImporting] = useState(false);
  const handleImportAll = async () => {
    if (parsedControls.length === 0) return;

    setIsImporting(true);
    let successCount = 0;
    let errorCount = 0;

    for (const control of parsedControls) {
      try {
        await createControlMutation.mutateAsync({
          frameworkId,
          controlId: control.controlId,
          title: control.title,
          description: control.description || control.title,
        });
        successCount++;
      } catch {
        errorCount++;
      }
    }

    setIsImporting(false);

    if (successCount > 0) {
      toast.success(`Successfully added ${successCount} control${successCount > 1 ? 's' : ''}`);
      setParsedControls([]);
      setBulkText('');
    }
    if (errorCount > 0) {
      toast.error(`Failed to add ${errorCount} control${errorCount > 1 ? 's' : ''}`);
    }
  };

  // Navigate back to framework page
  const handleDone = () => {
    navigate({
      to: '/workspaces/$workspaceId/compliance/$frameworkId',
      params: { workspaceId, frameworkId },
    });
  };

  return (
    <Main>
      <MainHeader />

      <div className="mx-auto box-content flex max-w-[1008px] flex-col px-6 pb-12">
        {/* Breadcrumb */}
        <div className="mb-4 flex items-center gap-2 text-sm">
          <Link
            to="/workspaces/$workspaceId/compliance"
            params={{ workspaceId }}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            Tests
          </Link>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
          <Link
            to="/workspaces/$workspaceId/compliance/$frameworkId"
            params={{ workspaceId, frameworkId }}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            {frameworkQuery.isLoading ? (
              <Skeleton className="h-4 w-24 inline-block" />
            ) : (
              framework?.name || 'Framework'
            )}
          </Link>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
          <span className="text-foreground">Manage Controls</span>
        </div>

        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <div className="text-foreground mb-2 text-2xl leading-6 font-semibold">
              Manage Controls
            </div>
            <div className="text-muted-foreground text-sm leading-5">
              Add, edit, or remove controls for {framework?.name || 'this framework'}.
            </div>
          </div>
          <Button variant="outline" onClick={handleDone}>
            Done
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'add' | 'bulk' | 'existing' | 'edit')}>
          <TabsList className="mb-6">
            <TabsTrigger value="add">
              <Plus className="h-4 w-4 " />
              Add Control
            </TabsTrigger>
            <TabsTrigger value="bulk">
              <FileText className="h-4 w-4 " />
              Bulk Import
            </TabsTrigger>
            <TabsTrigger value="existing">
              Existing Controls
              <Badge variant="secondary" className="ml-1">
                {existingControls.length}
              </Badge>
            </TabsTrigger>
            {framework?.canManage && (
              <TabsTrigger value="edit">
                <Pencil className="h-4 w-4 " />
                Edit Framework
              </TabsTrigger>
            )}
          </TabsList>

          {/* Add Single Control */}
          <TabsContent value="add" className="space-y-6">
            <div className="w-full space-y-4">
              <div className="space-y-2">
                <Label htmlFor="controlId">Control ID *</Label>
                <Input
                  id="controlId"
                  placeholder="e.g., CC6.1, AC-01, CTRL-001"
                  value={controlId}
                  onChange={(e) => setControlId(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Unique identifier for this control
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  placeholder="e.g., Logical Access Controls"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  placeholder="Describe the control objective and requirements..."
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <Button
                onClick={handleAddControl}
                disabled={createControlMutation.isPending}
              >
                {createControlMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin " />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    Add Control
                  </>
                )}
              </Button>
            </div>
          </TabsContent>

          {/* Bulk Import */}
          <TabsContent value="bulk" className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bulkText">Paste Controls</Label>
                <Textarea
                  id="bulkText"
                  placeholder={`Paste your controls here. Supported formats:

CC6.1 - Logical and Physical Access Controls
The entity implements logical access security software...

CC6.2: System Operations
The entity monitors system components...

Or simply:
CTRL-001 Access Control Policy
CTRL-002 Password Requirements`}
                  rows={10}
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Each control should have an ID followed by a title. Use "-" or ":" as separator.
                  Description can follow on subsequent lines until an empty line.
                </p>
              </div>

              <Button onClick={handleParseBulk} variant="outline">
                Parse Controls
              </Button>

              {parseError && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 text-sm">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <pre className="whitespace-pre-wrap">{parseError}</pre>
                </div>
              )}

              {parsedControls.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium">
                      Parsed Controls ({parsedControls.length})
                    </h3>
                    <Button
                      onClick={handleImportAll}
                      disabled={isImporting}
                    >
                      {isImporting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Plus className="h-4 w-4" />
                          Import All ({parsedControls.length})
                        </>
                      )}
                    </Button>
                  </div>

                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {parsedControls.map((control) => (
                      <div
                        key={control.id}
                        className="flex items-start gap-3 p-3 rounded-lg border bg-card"
                      >
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2">
                            <Input
                              value={control.controlId}
                              onChange={(e) =>
                                handleEditParsedControl(control.id, 'controlId', e.target.value)
                              }
                              className="w-32 h-8 text-sm font-mono"
                              placeholder="ID"
                            />
                            <Input
                              value={control.title}
                              onChange={(e) =>
                                handleEditParsedControl(control.id, 'title', e.target.value)
                              }
                              className="flex-1 h-8 text-sm"
                              placeholder="Title"
                            />
                          </div>
                          <Textarea
                            value={control.description}
                            onChange={(e) =>
                              handleEditParsedControl(control.id, 'description', e.target.value)
                            }
                            className="text-sm"
                            rows={2}
                            placeholder="Description"
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={() => handleRemoveParsedControl(control.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Existing Controls */}
          <TabsContent value="existing" className="space-y-4">
            {controlsTreeQuery.isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : existingControls.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No controls yet. Add your first control above.
              </div>
            ) : (
              <div className="space-y-2">
                {existingControls.map((control) => (
                  <div
                    key={control.id}
                    className={cn(
                      'flex items-start justify-between gap-4 p-4 rounded-lg border bg-card',
                      control.isCustom && 'border-primary/20'
                    )}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-medium">
                          {control.controlId}
                        </span>
                        <span className="text-muted-foreground">-</span>
                        <span className="font-medium truncate">{control.name}</span>
                        {control.isCustom && (
                          <Badge variant="outline" className="text-xs">
                            Custom
                          </Badge>
                        )}
                      </div>
                      {control.description && (
                        <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                          {control.description}
                        </p>
                      )}
                    </div>
                    {control.isCustom && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Control</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{control.controlId} - {control.name}"?
                              This will also delete all associated tests and evidence.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              onClick={() => deleteControlMutation.mutate({ id: control.id })}
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Edit Framework */}
          {framework?.canManage && (
            <TabsContent value="edit" className="space-y-6">
              <div className="w-full space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="frameworkName">Framework Name *</Label>
                  <Input
                    id="frameworkName"
                    placeholder="e.g., My Custom Framework"
                    value={frameworkName}
                    onChange={(e) => handleFrameworkNameChange(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="frameworkDescription">Description</Label>
                  <Textarea
                    id="frameworkDescription"
                    placeholder="Describe what this framework covers..."
                    rows={4}
                    value={frameworkDescription}
                    onChange={(e) => handleFrameworkDescriptionChange(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Optional description to help identify this framework
                  </p>
                </div>

                <Button
                  onClick={handleSaveFramework}
                  disabled={updateFrameworkMutation.isPending || !isFrameworkDirty}
                >
                  {updateFrameworkMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Main>
  );
}
